import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
function SectionLabel({
  text,
  dark = false
}: {
  text: string;
  dark?: boolean;
}) {
  return <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6 w-full" data-id="element-1">
      <span className={`font-dm-mono text-[9px] uppercase tracking-wider font-light whitespace-nowrap ${dark ? 'text-vault-dark' : 'text-vault-muted'}`} data-id="element-2">
        {text}
      </span>
      <div className={`flex-grow h-[1px] ${dark ? 'bg-vault-dark/20' : 'bg-vault-border'}`} data-id="element-3" />
    </motion.div>;
}
function Eyebrow({
  text,
  withLine = false
}: {
  text: string;
  withLine?: boolean;
}) {
  return <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-3" data-id="element-4">
      {withLine && <div className="w-6 h-[1px] bg-vault-lime/60" data-id="element-5" />}
      <span className="font-dm-mono text-[10px] uppercase tracking-wide text-vault-lime/70 font-light" data-id="element-6">
        {text}
      </span>
    </motion.div>;
}
function Logo({
  muted = false
}: {
  muted?: boolean;
}) {
  const textColor = muted ? 'text-white/20' : 'text-white';
  const bracketColor = muted ? 'text-white/10' : 'text-vault-lime';
  return <div className={`font-barlow-condensed font-black text-lg tracking-tight ${textColor}`} data-id="element-7">
      VAULT<span className={`font-dm-mono font-light ${bracketColor}`} data-id="element-8">[</span>
      LAB<span className={`font-dm-mono font-light ${bracketColor}`} data-id="element-9">]</span>
    </div>;
}
export function PitchDeck() {
  const [activeSlide, setActiveSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const scrollPosition = containerRef.current.scrollTop;
      const windowHeight = window.innerHeight;
      const currentSlide = Math.round(scrollPosition / windowHeight);
      setActiveSlide(currentSlide);
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, {
        passive: true
      });
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const scrollToSlide = (index: number) => {
    if (!containerRef.current) return;
    containerRef.current.scrollTo({
      top: index * window.innerHeight,
      behavior: 'smooth'
    });
  };
  return <div className="relative h-[100dvh] w-full bg-vault-dark font-barlow overflow-hidden selection:bg-vault-lime selection:text-vault-dark" data-id="element-10">
      {/* Dot Navigation — tappable 44px touch targets */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-1 pr-[max(env(safe-area-inset-right),_8px)]" data-id="element-11">
        {[0, 1, 2, 3, 4].map(index => <button key={index} onClick={() => scrollToSlide(index)} className="w-11 h-11 flex items-center justify-center" aria-label={`Go to slide ${index + 1}`} data-id="element-12">
            <div className={`rounded-full transition-all duration-500 ${activeSlide === index ? 'w-2 h-2 bg-vault-lime' : `w-1.5 h-1.5 ${activeSlide === 1 || activeSlide === 2 ? 'bg-vault-dark/30' : 'bg-white/20'}`}`} data-id="element-13" />
          </button>)}
      </div>

      {/* Scroll Container */}
      <div ref={containerRef} className="h-full w-full overflow-y-scroll snap-y snap-mandatory hide-scrollbar overscroll-y-contain" style={{
      WebkitOverflowScrolling: 'touch'
    }} data-id="element-14">
        {/* ── SLIDE 1: COVER ── */}
        <section className="h-[100dvh] w-full snap-start snap-always relative flex flex-col justify-between px-5 sm:px-7 pt-[max(env(safe-area-inset-top),_2.5rem)] pb-[max(env(safe-area-inset-bottom),_1.5rem)] dot-grid" data-id="element-15">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="flex justify-between items-start w-full" data-id="element-16">
            <motion.div variants={fadeInUp} data-id="element-17">
              <Logo data-id="element-18" />
            </motion.div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="flex-grow flex flex-col justify-center py-6" data-id="element-19">
            <Eyebrow text="Prepared Exclusively For" withLine data-id="element-20" />
            <motion.h1 variants={fadeInUp} className="font-barlow-condensed font-black text-[clamp(40px,12vw,80px)] text-white uppercase leading-display tracking-tight mb-5" data-id="element-21">
              LUCA
              <br data-id="element-22" />
              PIGNALOSA
            </motion.h1>
            <motion.div variants={fadeInUp} className="flex items-center gap-4" data-id="element-23">
              <span className="font-dm-mono text-[9px] text-vault-muted uppercase tracking-wide font-light" data-id="element-24">
                Abercrombys · Melbourne
              </span>
            </motion.div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} data-id="element-25">
            <motion.div variants={fadeInUp} className="w-full h-[1px] bg-vault-lime/20 mb-4" data-id="element-26" />
            <motion.p variants={fadeInUp} className="text-vault-muted text-[14px] font-light leading-body pr-10" data-id="element-27">
              This was built for agents who understand that{' '}
              <span className="text-white font-normal" data-id="element-28">
                presentation is everything.
              </span>
            </motion.p>
          </motion.div>
        </section>

        {/* ── SLIDE 2: THE OPPORTUNITY ── */}
        <section className="h-[100dvh] w-full snap-start snap-always bg-vault-light flex flex-col px-5 sm:px-7 pt-[max(env(safe-area-inset-top),_2rem)] pb-[max(env(safe-area-inset-bottom),_2rem)] justify-center" data-id="element-29">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="max-w-xl" data-id="element-30">
            <SectionLabel text="The Opportunity" dark data-id="element-31" />

            <motion.h2 variants={fadeInUp} className="font-barlow-condensed font-black text-[clamp(32px,9vw,64px)] uppercase leading-display tracking-tight mb-6 text-vault-dark" data-id="element-32">
              YOUR LISTINGS
              <br data-id="element-33" />
              DESERVE <span className="text-vault-lime-dark" data-id="element-34">BETTER.</span>
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-vault-dark/70 text-[15px] font-light leading-body pr-10" data-id="element-35">
              Property marketing hasn't changed in a decade. Portal listings.
              Static photography. Same template as every other agent or
              business. We think your listings - and your reputation - deserve
              something that actually reflects the quality of what you're
              selling.
            </motion.p>
          </motion.div>
        </section>

        {/* ── SLIDE 3: WHAT THIS GIVES YOU ── */}
        <section className="h-[100dvh] w-full snap-start snap-always bg-vault-light flex flex-col px-5 sm:px-7 pt-[max(env(safe-area-inset-top),_2rem)] pb-[max(env(safe-area-inset-bottom),_2rem)] justify-center" data-id="element-36">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="max-w-xl w-full" data-id="element-37">
            <SectionLabel text="In Practice" dark data-id="element-38" />

            <div className="flex flex-col gap-2.5" data-id="element-39">
              {[{
              num: '01',
              title: 'THE VENDOR MEETING',
              body: <>
                      Your next listing pitch includes something{' '}
                      <strong className="font-medium text-vault-dark" data-id="element-40">
                        no other agent in the room can show.
                      </strong>{' '}
                      A microsite built around that specific property. Before it
                      goes live. Before they've signed with anyone.
                    </>
            }, {
              num: '02',
              title: 'THE PRIVATE WINDOW',
              body: <>
                      Share it with your buyer database{' '}
                      <strong className="font-medium text-vault-dark" data-id="element-41">
                        before the listing goes public.
                      </strong>{' '}
                      Serious enquiry before day one. The momentum that makes a
                      campaign feel inevitable.
                    </>
            }, {
              num: '03',
              title: 'ZERO EXTRA WORK',
              body: <>
                      Send us the brief.{' '}
                      <strong className="font-medium text-vault-dark" data-id="element-42">
                        Live in 48 hours.
                      </strong>{' '}
                      Design, copy, animation, domain. We handle everything.
                      Nothing lands on you or your team.
                    </>
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="relative pl-5 py-2.5 pr-3 bg-white/40 border border-vault-dark/5" data-id="element-43">
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-vault-lime-dark" data-id="element-44" />
                  <div className="flex gap-3 items-start" data-id="element-45">
                    <span className="font-dm-mono text-[10px] text-vault-dark/30 mt-0.5 font-light shrink-0" data-id="element-46">
                      {item.num}
                    </span>
                    <div className="min-w-0" data-id="element-47">
                      <h3 className="font-barlow-condensed font-black text-[16px] text-vault-dark uppercase tracking-tight mb-1" data-id="element-48">
                        {item.title}
                      </h3>
                      <p className="text-vault-dark/60 text-[12px] font-light leading-body" data-id="element-49">
                        {item.body}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </section>

        {/* ── SLIDE 4: HOW IT WORKS ── */}
        <section className="h-[100dvh] w-full snap-start snap-always bg-vault-dark flex flex-col px-5 sm:px-7 pt-[max(env(safe-area-inset-top),_2rem)] pb-[max(env(safe-area-inset-bottom),_2rem)] justify-center dot-grid" data-id="element-50">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="max-w-xl w-full" data-id="element-51">
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6 w-full" data-id="element-52">
              <span className="font-dm-mono text-[9px] uppercase tracking-wide font-light text-vault-lime whitespace-nowrap" data-id="element-53">
                How It Works
              </span>
              <div className="flex-grow h-[1px] bg-vault-border" data-id="element-54" />
            </motion.div>

            <div className="flex flex-col gap-1.5 mb-5" data-id="element-55">
              {[{
              num: '01',
              bold: 'Send the brief',
              rest: ' — address, photos, key details. Takes 5 minutes.'
            }, {
              num: '02',
              bold: 'We build everything',
              rest: ' — AI-animated hero visual, copy, design, custom domain.'
            }, {
              num: '03',
              bold: 'You get a live link in 48 hours',
              rest: ' — ready to share with your buyer database privately.'
            }, {
              num: '04',
              bold: 'Buyers enquire before Day 1',
              rest: ' — you walk into the campaign with momentum already built.'
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-vault-contrast p-3.5 flex items-start gap-3 border border-vault-border" data-id="element-56">
                  <span className="font-dm-mono text-[10px] text-vault-lime font-light mt-0.5 shrink-0" data-id="element-57">
                    {item.num}
                  </span>
                  <p className="text-vault-muted text-[12px] sm:text-[13px] font-light leading-body flex-grow min-w-0" data-id="element-58">
                    <span className="text-white font-normal" data-id="element-59">{item.bold}</span>
                    {item.rest}
                  </p>
                  <span className="text-vault-muted/30 text-xs shrink-0" data-id="element-60">
                    →
                  </span>
                </motion.div>)}
            </div>

            <motion.div variants={fadeInUp} data-id="element-61">
              <a href="https://78-hopetoun-avenue-toorak.vaultlab.agency/" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between w-full bg-vault-lime text-vault-dark p-4 active:scale-[0.98] transition-transform duration-150" data-id="element-62">
                <div data-id="element-63">
                  <div className="font-barlow-condensed font-black text-[16px] uppercase tracking-tight" data-id="element-64">
                    View the Live Example
                  </div>
                  <div className="font-dm-mono text-[10px] text-vault-dark/50 font-light mt-0.5" data-id="element-65">
                    See exactly what your buyers will see
                  </div>
                </div>
                <span className="text-vault-dark text-xl shrink-0 ml-4" data-id="element-66">→</span>
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* ── SLIDE 5: CTA ── */}
        <section className="h-[100dvh] w-full snap-start snap-always relative flex flex-col justify-center px-5 sm:px-7 pt-[max(env(safe-area-inset-top),_2rem)] pb-[max(env(safe-area-inset-bottom),_2rem)] dot-grid" data-id="element-67">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="max-w-xl w-full z-10" data-id="element-68">
            <motion.p variants={fadeInUp} className="text-white text-[clamp(20px,5.5vw,32px)] font-light leading-[1.45] mb-4 pr-6" data-id="element-69">
              If one of your listings came to mind while you were reading this —{' '}
              <span className="text-vault-lime font-normal" data-id="element-70">
                that's the one.
              </span>
            </motion.p>

            <motion.p variants={fadeInUp} className="text-vault-muted text-[15px] font-light leading-body mb-8 pr-10" data-id="element-71">
              Send it through. We'll build a concept and show you what it looks
              like before you commit to anything.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col gap-2" data-id="element-72">
              <span className="font-dm-mono text-[9px] text-vault-muted uppercase tracking-wide font-light" data-id="element-73">
                Get in touch
              </span>
              <a href="mailto:hello@vaultlab.agency" className="text-vault-lime font-dm-mono text-base font-light active:opacity-60 transition-opacity py-1" data-id="element-74">
                hello@vaultlab.agency
              </a>
              <span className="text-vault-muted/50 font-dm-mono text-sm font-light" data-id="element-75">
                vaultlab.agency
              </span>
            </motion.div>
          </motion.div>

          {/* Footer */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="absolute bottom-[max(env(safe-area-inset-bottom),_1.5rem)] left-5 right-5 sm:left-7 sm:right-7" data-id="element-76">
            <motion.div variants={fadeInUp} className="w-full h-[1px] bg-vault-border mb-4" data-id="element-77" />
            <motion.div variants={fadeInUp} className="flex justify-between items-end gap-4" data-id="element-78">
              <Logo muted data-id="element-79" />
              <div className="text-right font-dm-mono text-[7px] sm:text-[8px] text-vault-muted/40 uppercase tracking-wide leading-relaxed max-w-[180px] font-light" data-id="element-80">
                Confidential · Prepared for Luca Pignalosa · Abercrombys
                <br data-id="element-81" />
                Not for distribution
              </div>
            </motion.div>
          </motion.div>
        </section>
      </div>
    </div>;
}